// src/lib/status.js — badges de status + cálculo de % executado.
// Portado dos helpers globais do monolito (calcPctObra ponderado por nome de etapa).

import { esc } from './dom.js';

// ── Status de obra ──────────────────────────────────────
const OBRA_STATUS_LABEL = {
  planejamento: 'Em planejamento',
  em_andamento: 'Em andamento',
  pausada: 'Pausada',
  concluida: 'Concluída',
  atrasada: 'Atrasada',
  cancelada: 'Cancelada',
};

const OBRA_STATUS_CLASS = {
  planejamento: 'badge-gray',
  em_andamento: 'badge-blue',
  pausada: 'badge-amber',
  concluida: 'badge-green',
  atrasada: 'badge-red',
  cancelada: 'badge-red',
};

export function statusLabel(s) {
  return OBRA_STATUS_LABEL[s] || s || '—';
}

export function statusBadge(s) {
  const cls = OBRA_STATUS_CLASS[s] || 'badge-gray';
  return `<span class="badge ${cls}" style="border-radius:20px;padding:4px 12px;font-size:11px;font-weight:500">${esc(statusLabel(s))}</span>`;
}

// ── Cálculo de % executado de obra (fiel ao monolito) ───
// Pondera pelo PESO PADRÃO da etapa, casado pelo NOME (não pela coluna peso do
// banco). Se todas as etapas casam um peso conhecido → média ponderada por esse
// peso. Caso contrário (obra com etapas customizadas) → média simples.
const PESOS_ETAPAS_OBRA = [
  { keys: ['planejamento', 'projeto'], peso: 3, label: 'Planejamento' },
  { keys: ['regulariza', 'licença', 'licenca', 'alvará', 'alvara'], peso: 2, label: 'Regularização' },
  { keys: ['prepara', 'terreno', 'limpeza', 'terraplan'], peso: 5, label: 'Preparação' },
  { keys: ['fundação', 'fundacao', 'sapata', 'baldrame'], peso: 15, label: 'Fundação' },
  { keys: ['estrutura', 'pilar', 'viga', 'laje'], peso: 20, label: 'Estrutura' },
  { keys: ['alvenaria', 'vedação', 'vedacao', 'parede'], peso: 10, label: 'Alvenaria' },
  { keys: ['instalação', 'instalacao', 'instalações', 'instalacoes', 'elétric', 'eletric', 'hidráulic', 'hidraulic', 'hidrossanit'], peso: 10, label: 'Instalações' },
  { keys: ['execução', 'execucao', 'cobertura', 'telhado'], peso: 5, label: 'Execução' },
  { keys: ['acabamento', 'pintura', 'piso', 'azulejo', 'revestimento'], peso: 25, label: 'Acabamento' },
  { keys: ['entrega', 'vistoria', 'conclusão', 'conclusao', 'final'], peso: 5, label: 'Entrega' },
];

function _matchPesoEtapa(nomeEtapa) {
  const nome = (nomeEtapa || '').toLowerCase();
  return PESOS_ETAPAS_OBRA.find((p) => p.keys.some((k) => nome.includes(k)));
}

/**
 * @param {Array} etapas — array de { progresso, nome_etapa }
 */
export function calcPctObra(etapas) {
  if (!etapas?.length) return 0;

  // Avanço FÍSICO ponderado pelo CUSTO (Valor Agregado ÷ orçamento) — critério técnico do
  // cronograma físico-financeiro / EVM. Peso de cada etapa = sua fatia do custo.
  // SÓ se aplica quando TODAS as etapas têm orçamento (valor_etapa > 0): se alguma estiver
  // sem valor, seu custo entraria como zero e distorceria o avanço (ex.: obra com 3 de 11
  // etapas orçadas e essas 3 concluídas marcaria 100% por engano).
  // Ver docs/CRITERIO-AVANCO-FISICO-FINANCEIRO.md.
  const todasComValor = etapas.every((e) => Number(e.valor_etapa || 0) > 0);
  if (todasComValor) {
    const somaValor = etapas.reduce((s, e) => s + Number(e.valor_etapa), 0);
    const ev = etapas.reduce((s, e) => s + Number(e.progresso || 0) * Number(e.valor_etapa), 0);
    return Math.round(ev / somaValor);
  }

  // Orçamento incompleto/ausente → média simples (transparente; não usa o peso manual,
  // que é fonte de erro). Ao completar os valores das etapas, passa a ponderar por custo.
  return Math.round(etapas.reduce((s, e) => s + Number(e.progresso || 0), 0) / etapas.length);
}

// True se alguma etapa não tem orçamento (valor_etapa > 0). Nesse caso o avanço físico é
// por média simples até completar os valores. Ver CRITERIO-AVANCO-FISICO-FINANCEIRO.md.
export function orcamentoIncompleto(etapas) {
  if (!etapas?.length) return false;
  return etapas.some((e) => !(Number(e.valor_etapa || 0) > 0));
}
